/**
 * /market/:slug/reset-password — Nueva contraseña para clientes B2C (Fase C).
 * Llegada desde el email de sendCustomerPasswordResetEmail (?token=...).
 * Mismo theme/branding del tenant que /market/:slug/cuenta.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { getRequest } from "@tanstack/react-start/server";
import { customerFetch } from "@/lib/market-customer-auth";
import { MarketPageSkeleton } from "@/components/market/MarketSkeletons";
import { Field } from "@/routes/market_.$slug.cuenta";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> =>
    loadMarketCatalog(slug, { request: getRequest() }),
  );

export const Route = createFileRoute("/market_/$slug/reset-password")({
  pendingComponent: MarketPageSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: ({ params }) => ({
    meta: [
      { title: `Nueva contraseña — ${params.slug}` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function ResetPasswordPage() {
  const { slug } = Route.useParams();
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const result = Route.useLoaderData() as MarketCatalogResult;
  const data = result.ok ? result.data : null;

  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const offlineScreen = renderMarketPublicOffline(result);
  if (offlineScreen) return offlineScreen;

  if (!data) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
        <Package className="h-12 w-12 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Tienda no disponible</h1>
      </div>
    );
  }

  const { tenant } = data;
  const brandStyle = data.brand.surfaceStyle;
  const experienceBrand = data.experiencePublished?.brand;
  const tenantName = experienceBrand?.name?.trim() || tenant.name;
  const logoUrl = experienceBrand?.logo?.light?.src || tenant.logoUrl;

  const submit = async () => {
    setError(null);
    if (!token) { setError("El enlace no tiene token. Solicitá uno nuevo desde el login."); return; }
    if (password.length < 6) { setError("La contraseña debe tener al menos 6 caracteres."); return; }
    if (password !== password2) { setError("Las contraseñas no coinciden."); return; }
    setBusy(true);
    try {
      const res = await customerFetch(slug, `/api/market/${slug}/account?action=reset`, {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      const d = (await res.json()) as { ok?: boolean; error?: string };
      if (!res.ok || !d.ok) { setError(d.error ?? "No se pudo actualizar la contraseña."); return; }
      setDone(true);
      toast.success("Contraseña actualizada");
      setTimeout(() => void navigate({ to: "/market/$slug/cuenta", params: { slug } }), 1500);
    } catch {
      setError("Error de red. Verificá tu conexión e intentá de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="market-surface flex min-h-[100dvh] flex-col"
      style={{
        ...brandStyle,
        backgroundImage:
          "radial-gradient(ellipse 90% 55% at 50% -15%, color-mix(in srgb, var(--market-accent) 16%, transparent), transparent 65%), linear-gradient(180deg, var(--market-bg), color-mix(in srgb, var(--market-accent) 5%, var(--market-bg)))",
        backgroundColor: "var(--market-bg)",
      }}
    >
      <header className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 border-b border-[var(--market-surface-variant)] bg-[var(--market-surface)]/90 px-4 backdrop-blur-md md:h-16 md:px-12">
        <Link
          to="/market/$slug/cuenta"
          params={{ slug }}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[var(--market-on-surface-variant)] transition-colors hover:bg-[var(--market-surface-bright)]"
          aria-label="Volver al login"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-lg object-contain bg-[var(--market-surface-bright)] p-0.5 ring-1 ring-[var(--market-surface-variant)]"
            />
          ) : null}
          <p className="market-font-display truncate text-base font-bold leading-tight text-[var(--market-accent)] md:text-lg">
            {tenantName}
          </p>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8">
        <div
          className="animate-fade-up w-full max-w-md rounded-2xl border border-[var(--market-outline)] bg-[var(--market-surface-bright)] p-6 shadow-sm md:p-8"
          style={{ animationDuration: "0.45s" }}
        >
          <h1 className="market-font-display text-2xl font-extrabold leading-tight text-[var(--market-on-surface)]">
            Nueva contraseña
          </h1>
          <p className="mt-1.5 text-sm text-[var(--market-on-surface-variant)]">
            Elegí una contraseña nueva para tu cuenta en {tenantName}.
          </p>

          {done ? (
            <p className="mt-6 rounded-xl border border-[var(--market-outline)] bg-[var(--market-surface)] p-4 text-sm text-[var(--market-on-surface)]">
              Contraseña actualizada. Redirigiendo al login…
            </p>
          ) : (
            <div className="mt-6 space-y-3.5">
              <Field label="Nueva contraseña" type="password" value={password} onChange={setPassword} />
              <Field label="Repetir contraseña" type="password" value={password2} onChange={setPassword2} />
              {error && <p className="text-xs text-destructive">{error}</p>}
              <button
                type="button"
                disabled={busy}
                onClick={submit}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--market-accent)] py-3.5 text-base font-bold text-[var(--primary-foreground)] transition-transform active:scale-[0.98] disabled:opacity-50"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Guardar contraseña
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
